/**
 * <BrandMark>
 * ----------
 * Single source of truth for rendering the Eonus company logo. The asset
 * (`logo.png`) is a colour mark with a *dark* wordmark and the registered ®
 * symbol — so on dark surfaces (the default in this app) we wrap it in a soft
 * white "chip" to guarantee legibility while preserving the original colours.
 *
 * Variants:
 *   - `chip`  (default): white rounded surface with the logo inside. Best for
 *                        dark page backgrounds (login hero, sidebar header).
 *   - `bare`           : just the image, no surrounding surface. Use on
 *                        already-light backgrounds.
 *
 * Sizes (height of the rendered logo, width is auto):
 *   - `xs` 20px     — table rows, dense badges
 *   - `sm` 28px     — sidebar header
 *   - `md` 40px     — login secondary slot
 *   - `lg` 56px     — login hero
 *   - `xl` 72px     — splash / empty states
 *
 * Pulse:
 *   When `pulse` is on (default) and the variant is `chip`, two staggered
 *   ripple rings (energy-green + electric-cyan) emanate from the chip and a
 *   soft inner halo "breathes" — evoking an EV charging cycle. The animation
 *   is gated behind `motion-safe:` so users who set `prefers-reduced-motion`
 *   see a static brand mark.
 *
 * The component is intentionally framework-agnostic about *where* the asset
 * lives: callers pass `src` (typically `"/logo.png"` resolved from each app's
 * `public/` folder so Vite serves it at the root). A sensible default of
 * `"/logo.png"` is provided to keep call-sites tidy.
 */
import type { HTMLAttributes } from 'react';
import { cn } from '@eon/utils';

export type BrandMarkSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type BrandMarkVariant = 'chip' | 'bare';

export interface BrandMarkProps extends HTMLAttributes<HTMLSpanElement> {
  src?: string;
  alt?: string;
  size?: BrandMarkSize;
  variant?: BrandMarkVariant;
  /** Animated "charging" ripple around the chip. Defaults to `true`. */
  pulse?: boolean;
}

const HEIGHT_PX: Record<BrandMarkSize, number> = {
  xs: 20,
  sm: 28,
  md: 40,
  lg: 56,
  xl: 72,
};

const CHIP_PADDING: Record<BrandMarkSize, string> = {
  xs: 'px-1.5 py-1',
  sm: 'px-2 py-1.5',
  md: 'px-3 py-2',
  lg: 'px-4 py-2.5',
  xl: 'px-5 py-3',
};

const CHIP_RADIUS: Record<BrandMarkSize, string> = {
  xs: 'rounded-md',
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
  xl: 'rounded-2xl',
};

function defaultLogoSrc(): string {
  // `import.meta.env` is injected by Vite; this package's tsconfig has no
  // vite/client types, so narrow the type locally instead.
  const meta = import.meta as { env?: { BASE_URL?: string } };
  const base = meta.env?.BASE_URL ?? '/';
  return `${base}logo.png`;
}

export function BrandMark({
  src = defaultLogoSrc(),
  alt = 'Eonus',
  size = 'md',
  variant = 'chip',
  pulse = true,
  className,
  ...rest
}: BrandMarkProps) {
  const height = HEIGHT_PX[size];
  const img = (
    <img
      src={src}
      alt={alt}
      width={Math.round(height * (131 / 109))}
      height={height}
      style={{ height, width: 'auto' }}
      className="relative z-10 block select-none"
      draggable={false}
      decoding="async"
      loading="eager"
    />
  );

  if (variant === 'bare') {
    return (
      <span
        {...rest}
        className={cn('inline-flex items-center', className)}
        aria-label={alt}
      >
        {img}
      </span>
    );
  }

  // Chip variant. The outer wrapper is `relative` so that the absolutely
  // positioned ripple rings can sit behind the chip surface while still
  // getting clipped to the same rounded shape.
  return (
    <span
      {...rest}
      aria-label={alt}
      className={cn('relative inline-flex items-center justify-center', className)}
    >
      {pulse ? (
        <>
          {/*
           * Outer ripple ring 1 — energy green. Sits at the same bounds as
           * the chip and animates outward. `pointer-events-none` keeps it
           * inert; `motion-safe:` honours prefers-reduced-motion.
           */}
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 ring-2 ring-energy-400/70',
              'motion-safe:animate-charge-ping',
              CHIP_RADIUS[size],
            )}
          />
          {/* Outer ripple ring 2 — electric cyan, half a cycle later. */}
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 ring-2 ring-electric-400/70',
              'motion-safe:animate-charge-ping-delayed',
              CHIP_RADIUS[size],
            )}
          />
        </>
      ) : null}

      {/* The chip surface itself */}
      <span
        className={cn(
          'relative z-10 inline-flex items-center justify-center bg-white shadow-sm ring-1 ring-black/5',
          pulse ? 'motion-safe:animate-charge-halo' : 'transition-shadow duration-200',
          CHIP_PADDING[size],
          CHIP_RADIUS[size],
        )}
      >
        {img}
      </span>
    </span>
  );
}
